import axios from 'axios';
import RestClient from '../../api/axiosInterceptor';
import { toast } from 'react-toastify';

/**
 * Displays a toast notification based on the given intent and message.
 *
 * @param {object} toast - The toast object used to display notifications.
 * @param {string} intent - The intent of the toast ('success', 'error', 'info', 'warning').
 * @param {string} message - The message to display in the toast notification.
 * @throws {Error} Throws an error if an invalid intent is provided or there is an issue displaying the toast.
 */
export const showToast = (toast, intent, message) => {
	try {
		// Ensure the intent is a valid type
		if (typeof intent !== 'string' || !message) {
			throw new Error(
				'Invalid input: intent must be a string and message is required.',
			);
		}

		// Determine which type of toast to show based on the intent
		switch (intent.toLowerCase()) {
			case 'success':
				toast.success(message);
				break;
			case 'error':
				toast.error(message);
				break;
			case 'info':
				toast.info(message);
				break;
			case 'warning':
				toast.warning(message);
				break;
			default:
				toast(message);
		}
	} catch (error) {
		console.error('Error occurred while showing toast: ', error.message);
		throw new Error(`Failed to show toast: ${error.message}`);
	}
};

/**
 * Verifies the reCAPTCHA token with the backend.
 *
 * @param {string} captchaValue - The reCAPTCHA token to verify.
 * @param {function} setError - The function to set error state.
 * @returns {Promise<boolean>} - Returns a promise that resolves to a boolean indicating the verification result.
 */
export const verifyRecaptcha = async (captchaValue, setError) => {
	try {
		const formData = new URLSearchParams();
		formData.append('captchaValue', captchaValue);

		const response = await axios.post(
			`${process.env.BASE_API_URL}/hospital/verify-captcha/`,
			formData,
			{
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			},
		);

		if (response.data.success) {
			setError('reCAPTCHA successful.');
			return true;
		} else {
			setError(response.data.message || 'Invalid reCAPTCHA. Please try again.');
			return false;
		}
	} catch (error) {
		console.error('Error verifying reCAPTCHA:', error.message);
		setError('Failed to verify, please retry');
		return false;
	}
};

export const validateConfirmPassword = async (
	password,
	confirmPassword,
	setErrors,
) => {
	if (confirmPassword !== password) {
		setErrors((prevErrors) => ({
			...prevErrors,
			confirmPassword: 'Passwords must match!',
		}));
	} else {
		setErrors((prevErrors) => ({ ...prevErrors, confirmPassword: '' }));
	}
};

/**
 * Checks the completion status of a user's profile using Basic Authentication
 *
 * @async
 * @param {string} credentials - User's email address and password
 * @returns {Promise<Object>} Profile completion status
 * @property {boolean} isComplete - Whether the profile is complete
 * @property {Array<string>} incompleteFields - List of fields that need to be completed
 * @property {string|null} error - Error message if request fails, null otherwise
 */
export const checkProfileCompletionStatus = async (credentials) => {
	try {
		// Make request with Basic Auth header
		const response = await RestClient.get('/hospital/profile-summary/', {
			headers: {
				Authorization: `Basic ${credentials}`,
				'Content-Type': 'application/json',
			},
		});

		if (response.status === 200) {
			return {
				isComplete: true,
				incompleteFields: [],
				error: null,
			};
		}

		return {
			isComplete: false,
			incompleteFields: response.data?.incomplete_fields || [],
			error: null,
		};
	} catch (error) {
		const errorMessage =
			error.response?.data?.message || 'Failed to check profile completion';
		console.error('Profile completion check error:', error);
		return {
			isComplete: false,
			incompleteFields: [],
			error: errorMessage,
		};
	}
};

/**
 * @param {Object} preFilledData - The object containing prefilled data with snake_case property names.
 * @returns {Object} initialFormData - The structured data with camelCase keys.
 */
export const convertToInitialFormData = (preFilledData) => {
	console.log('preFilledData: ', preFilledData);
	return {
		personalInfo: {
			profilePicture: preFilledData.profilePicture || '',
			hospitalName: preFilledData.hospitalName || '',
			hospitalBio: preFilledData.hospitalBio || '',
			hospitalSpecialization: preFilledData.hospitalSpecialization || '',
		},
		contactInfo: {
			phoneNumber: preFilledData.phoneNumber || '',
			email: preFilledData.email || '',
			alternatePhoneNumber: preFilledData.alternatePhoneNumber || '',
			country: preFilledData.country || '',
			state: preFilledData.state || '',
			city: preFilledData.city || '',
			streetAddress: preFilledData.streetAddress || '',
			postalCode: preFilledData.postalCode || '',
		},
		staffInfo: {
			staffMembers: preFilledData.staffMembers || [
				{ name: '', position: '', email: '' },
			],
		},
		hospitalAcc: {
			hospitalAccreditations: preFilledData.hospitalAccreditations || [
				{
					file: null,
					documentName: '',
					issueDate: '',
					expiryDate: null,
				},
			],
		},
		facilities: {
			facilities: preFilledData.facilities || [
				{
					name: '',
					description: '',
					capacity: '',
					availability: 'Available',
				},
			],
		},
		socialsInfo: {
			facebook: preFilledData.facebook || '',
			twitter: preFilledData.twitter || '',
			linkedin: preFilledData.linkedin || '',
			instagram: preFilledData.instagram || '',
		},
	};
};

/**
 * Converts a string to title case format, handling special cases
 * @param {string} str - The string to convert
 * @returns {string} The converted string in title case
 */
export const toTitleCase = (str) => {
	if (!str) return '';

	// Handle hyphenated words and multiple spaces
	return str
		.toLowerCase()
		.split(/[\s-]+/)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};
